<script setup lang="ts">
import {useNewUiEnabled} from '@/composable/useNewUiEnabled'
import {useModalStore} from '@/stores/modal'
import assert from '@/utils/assert'
import {isNullOrUndefined} from '@/utils/utils'
import {storeToRefs} from 'pinia'
import {computed, ref, toValue} from 'vue'
import {useI18n} from 'vue-i18n'
import RBButton from './Button.vue'

const {t} = useI18n()

const modalStore = useModalStore()
const {currentModal, hasModal} = storeToRefs(modalStore)
const isNewUiEnabled = useNewUiEnabled()

const isBusy = ref(false)

const submit = computed(() => currentModal.value?.submit || {})
const submitVisible = computed(() => (isNullOrUndefined(submit.value.visible) ? true : submit.value.visible))
const submitEnabled = computed(() => (isNullOrUndefined(submit.value.enabled) ? true : submit.value.enabled))
const submitLabel = computed(() => toValue(submit.value.label) || t('submit'))
const submitIcon = computed(() => {
  const icon = toValue(submit.value.icon)
  return icon === false ? null : icon || (isNewUiEnabled.value ? null : 'fa-solid fa-check-circle')
})
const submitVariant = computed(() => toValue(submit.value.variant) || (isNewUiEnabled.value ? 'new-primary' : 'success'))
const submitClass = computed(() => toValue(submit.value.class))

const cancel = computed(() => currentModal.value?.cancel || {})
const cancelVisible = computed(() => (isNullOrUndefined(cancel.value.visible) ? true : cancel.value.visible))
const cancelLabel = computed(() => toValue(cancel.value.label) || t('cancel'))
const cancelIcon = computed(() => {
  const icon = toValue(cancel.value.icon)
  return icon === false ? null : icon || (isNewUiEnabled.value ? null : 'fa-solid fa-circle-xmark')
})
const cancelVariant = computed(() => (isNewUiEnabled.value ? 'new-neutral' : toValue(cancel.value.variant) || 'warning'))
const cancelClass = computed(() => toValue(cancel.value.class))
const sizeClass = computed(() => {
  switch (currentModal.value?.size) {
    case 'medium':
      return 'w-[calc(100vw-24px)] max-w-[calc(100vw-24px)] new-sm:w-[350px] new-sm:min-w-min'
    case 'full':
      return 'w-[calc(100vw-24px)]'
    case 'extra-large':
      return 'w-[calc(100vw-24px)] max-w-[calc(100vw-24px)] new-sm:w-[850px] new-sm:min-w-min'
    case 'large':
    default:
      return 'w-[calc(100vw-24px)] max-w-[calc(100vw-24px)] new-sm:w-[600px] new-sm:min-w-min'
  }
})

async function handleCallback(callback?: () => void | Promise<void>) {
  assert(currentModal.value)
  const currentModalId = currentModal.value.id

  try {
    isBusy.value = true
    if (callback) {
      await callback()
    }
  } catch (error) {
    return console.error(`Handled callback with error ${error}`)
  } finally {
    isBusy.value = false
    if (currentModal.value && currentModalId === currentModal.value.id) {
      if (callback !== submit.value.callback ? true : currentModal.value.closeOnSubmit) {
        modalStore.clearModal()
      }
    }
  }
}
</script>

<template>
  <div
    v-if="hasModal && currentModal"
    class="fixed left-0 top-0 w-screen h-screen overflow-y-scroll bg-black/50 z-modal flex"
    :class="{'px-new-lg py-new-3xl new-sm:px-new-3xl': isNewUiEnabled}"
  >
    <div
      class="mx-auto my-auto flex flex-col bg-white"
      :class="[
        {
          [sizeClass]: isNewUiEnabled,
          'rounded-new-xl': isNewUiEnabled,
          'max-h-[calc(100vh-3rem)] overflow-y-auto': isNewUiEnabled && !currentModal.allowOverflow,
          'py-8 px-10': !currentModal.noPadding && !isNewUiEnabled,
          'gap-5': !currentModal.noGap && !isNewUiEnabled,
          'px-new-lg py-new-4xl new-sm:px-new-5xl': !currentModal.noPadding && isNewUiEnabled,
          'gap-new-3xl': !currentModal.noGap && isNewUiEnabled,
        },
      ]"
    >
      <div v-if="currentModal.title || currentModal.message">
        <h1
          v-if="currentModal.title"
          :class="{'text-darkgrey': !isNewUiEnabled, 'text-new-primary-default text-new-xl font-normal': isNewUiEnabled}"
        >
          {{ currentModal.title }}
        </h1>
        <p
          v-if="currentModal.message"
          :class="{
            'text-base text-darkgrey': !isNewUiEnabled,
            'text-new-md leading-relaxed text-new-neutral-600 mt-new-2xl': isNewUiEnabled,
          }"
        >
          {{ currentModal.message }}
        </p>
      </div>

      <div id="modal-dialog-content" class="contents" :class="{'text-new-base text-new-neutral-700': isNewUiEnabled}"></div>

      <div
        v-if="submitVisible || cancelVisible"
        class="flex gap-x-2 gap-y-1"
        :class="isNewUiEnabled ? 'flex-row' : 'flex-col sm:flex-row'"
      >
        <RBButton
          :class="{hidden: !submitVisible, [submitClass]: !!submitClass}"
          :is-disabled="isBusy || !submitEnabled || !submitVisible"
          :is-loading="isBusy"
          :label="submitLabel"
          :variant="submitVariant"
          @click="handleCallback(submit.callback)"
        >
          <i v-if="submitIcon" class="pr-4" :class="submitIcon"></i>
        </RBButton>
        <RBButton
          :class="{hidden: !cancelVisible, [cancelClass]: !!cancelClass}"
          :is-disabled="isBusy || !cancelVisible"
          :label="cancelLabel"
          :variant="cancelVariant"
          @click="handleCallback(cancel.callback)"
        >
          <i v-if="cancelIcon" class="pr-4" :class="cancelIcon"></i>
        </RBButton>
      </div>
    </div>
  </div>
</template>
